import { useEffect, useState } from "react";
import { useQuery } from "@apollo/client";
import { Select } from "antd";
import { EmptyState, ErrorState } from "../states";
import { useToast } from "../Toast/toastHook";
import ListSkeleton from "../states/ListSkeleton";
import { GET_COMPANY_UPCOMING_OPEN_HOUSES } from "../../graphql/listingQueries";
import {
  ALL_AGENTS,
  OPEN_HOUSE_SORT_OPTIONS,
  UPCOMING_WINDOW_DAYS,
} from "../../constants/upcomingListingsConstants";
import { canAssignOpenHouseHost, currentUser } from "../../utils/roles";
import AssignHostModal from "./AssignHostModal";
import OpenHouseRow from "./OpenHouseRow";

/**
 * Upcoming open houses for the office, one row per slot, for a broker to walk
 * through in the weekly sales meeting — and to fill the gaps on the spot.
 *
 * Unpaginated on purpose: a single office's open houses inside a 30-day window
 * is tens of rows, and a meeting list that stops at page one is worse than a
 * slightly longer response.
 */
const OpenHousesTab = ({
  companyId,
  agentId,
  agentOptions,
  onAgentChange,
  selectedAgentLabel,
  onClearAgent,
}) => {
  const toast = useToast(4000);
  const [sortBy, setSortBy] = useState("soonest");
  const [needsHostOnly, setNeedsHostOnly] = useState(false);
  const [assigning, setAssigning] = useState(null);

  const canAssign = canAssignOpenHouseHost(currentUser());

  const { data, previousData, loading, error, refetch } = useQuery(
    GET_COMPANY_UPCOMING_OPEN_HOUSES,
    {
      variables: {
        companyId,
        withinDays: UPCOMING_WINDOW_DAYS,
        needsHostOnly: needsHostOnly || undefined,
        agentId: agentId === ALL_AGENTS ? undefined : agentId,
        sortBy,
      },
      fetchPolicy: "cache-and-network",
    }
  );

  useEffect(() => {
    if (error) toast("error", error.message);
  }, [error, toast]);

  // Changing the sort or the filter changes the variables, and Apollo reports
  // `data` as undefined while that refetch is in flight. Falling back to the
  // previous result keeps the rows — and, more importantly, the controls —
  // on screen, so the toggle that started the refetch can still be undone.
  const payload =
    data?.getCompanyUpcomingOpenHouses ??
    previousData?.getCompanyUpcomingOpenHouses;
  const rows = payload?.rows ?? [];
  const needsHostCount = payload?.needsHostCount ?? 0;

  if (loading && !payload) return <ListSkeleton rows={6} />;
  if (error && !payload) return <ErrorState onRetry={() => refetch()} />;

  const empty = () => {
    if (needsHostOnly) {
      return (
        <EmptyState
          title="Every open house has a host"
          description="Nothing in the next 30 days is waiting on cover."
          actionLabel="Show all open houses"
          onAction={() => setNeedsHostOnly(false)}
        />
      );
    }
    return selectedAgentLabel ? (
      <EmptyState
        title="No open houses for this agent"
        description={`${selectedAgentLabel} has nothing scheduled in the next ${UPCOMING_WINDOW_DAYS} days.`}
        actionLabel="Show all agents"
        onAction={onClearAgent}
      />
    ) : (
      <EmptyState
        title="No upcoming open houses"
        description={`Nothing in this office is scheduled in the next ${UPCOMING_WINDOW_DAYS} days.`}
      />
    );
  };

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Select
          showSearch
          optionFilterProp="label"
          value={agentId}
          onChange={onAgentChange}
          options={agentOptions}
          style={{ minWidth: 200 }}
          aria-label="Filter by agent"
        />

        <Select
          value={sortBy}
          onChange={setSortBy}
          options={OPEN_HOUSE_SORT_OPTIONS}
          style={{ minWidth: 170 }}
          aria-label="Sort open houses"
        />

        <button
          type="button"
          aria-pressed={needsHostOnly}
          onClick={() => setNeedsHostOnly((v) => !v)}
          className={`rounded-sm border px-3 py-1 text-xs font-bold ${
            needsHostOnly
              ? "border-primary bg-[#f4f7e4] text-primary_dark"
              : "border-light_gray bg-white text-secondary"
          }`}
        >
          Needs a host
          <span className="ml-2 rounded-full bg-[#fdeceb] px-2 py-0.5 text-[10px] text-[#cd2026]">
            {needsHostCount}
          </span>
        </button>

        <span className="ml-auto rounded-sm border border-light_gray bg-white px-3 py-1 text-xs text-secondary">
          <b className="text-primary_dark">{rows.length}</b> open{" "}
          {rows.length === 1 ? "house" : "houses"}
        </span>
      </div>

      {rows.length === 0 ? (
        empty()
      ) : (
        <div className="overflow-hidden rounded-lg border border-light_gray bg-white shadow-card">
          {rows.map((occurrence) => (
            <OpenHouseRow
              key={occurrence.openHouseId}
              occurrence={occurrence}
              canAssign={canAssign}
              onAssign={setAssigning}
            />
          ))}
        </div>
      )}

      <AssignHostModal
        companyId={companyId}
        occurrence={assigning}
        onClose={() => setAssigning(null)}
        onAssigned={() => refetch()}
      />
    </div>
  );
};

export default OpenHousesTab;
